import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combine class names with conflict resolution. Tailwind's `bg-red-500 bg-blue-500`
 * collapses to just `bg-blue-500` rather than appending duplicates.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format a number as currency-style with explicit sign. */
export function fmtMoney(v: number | null | undefined, ccy = 'USD') {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
  return `${formatted} ${ccy}`;
}

/** Format a percentage with 2 decimals. */
export function fmtPct(v: number | null | undefined) {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${v.toFixed(2)}%`;
}

/**
 * Format an R-multiple for on-screen display: signed (leading `+` when
 * positive), 2 decimals, `R` suffix — e.g. `+1.25R`, `-0.50R`, `0.00R`.
 * Callers handle their own null/`—` case (the surrounding ternaries differ
 * per site). NOTE: the PDF export intentionally renders R unsigned, so it
 * does NOT use this helper.
 */
export function fmtRMultiple(v: number): string {
  return `${v > 0 ? '+' : ''}${v.toFixed(2)}R`;
}

/** Relative time ("3m ago"). */
export function timeAgo(iso: string | null | undefined) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'in the future';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * Format a Postgres `timestamptz` ISO string in the BROWSER'S LOCAL timezone
 * as `YYYY-MM-DD HH:MM:SS` (or `YYYY-MM-DD HH:MM` if `withSeconds=false`).
 *
 * Why we don't slice the raw ISO string: PostgREST returns timestamptz as
 * e.g. `"2026-05-07T17:34:48.123+00:00"` — that is UTC, with the offset
 * baked in. The old `iso.replace('T', ' ').slice(0, 19)` pattern stripped
 * the offset and rendered the UTC value as if it were local time, so a
 * trade filled at 20:34:48 broker time (17:34:48 UTC, broker on UTC+3)
 * showed as `2026-05-07 17:34:48` in the dashboard while the user's MT5
 * terminal showed `20:34:48`. Confusing.
 *
 * `new Date(iso)` correctly parses the `+00:00` offset (per lessons.md L44
 * about Postgres timestamptz), and the bare `getFullYear/getMonth/...`
 * accessors return the value in the browser's local timezone — exactly
 * what we want here.
 *
 * For UTC-stable formatting (PDF / tax exports / generated-at headers)
 * use `toISOString()` directly; this helper is for on-screen display only.
 */
export function fmtLocalDateTime(
  iso: string | null | undefined,
  withSeconds = true,
): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  if (!withSeconds) return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
  const ss = pad(d.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}
